import { ThemeProvider } from "@aws-amplify/ui-react";
import { useRoutes, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import { getCurrentUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import "@aws-amplify/ui-react/styles.css";
import "./App.css";
import { customTheme } from "./theme";

import Layout from "./components/Layout";
import routes from "./routes";
import CookieConsent from "./components/CookieConsent";
import AnalyticsTracker from "./components/AnalyticsTracker";
import AuthAnalyticsTracker from "./components/AuthAnalyticsTracker";
import AnalyticsStatus from "./components/AnalyticsStatus";
import { MockDataProvider } from "./contexts/MockDataContext";
import { RatingDisplayProvider } from "./contexts/RatingDisplayContext";
import { UserAttributesProvider } from "./contexts/UserAttributesContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";

// Single source of truth for public routes — used in both checkAuth and render
const PUBLIC_ROUTES = [
  "/",
  "/login",
  "/signup",
  "/terms",
  "/privacy",
  "/help",
  "/contact",
  "/skill-packages",
  "/frameworks",
  "/frameworks-modern",
  "/about",
  "/developer",
  "/enquiry-status",
  "/verify-contact",
  "/mcp-auth",
];

const isPublicPath = (pathname: string): boolean =>
  PUBLIC_ROUTES.includes(pathname) ||
  pathname.startsWith("/profiles/") ||
  pathname.startsWith("/share/") ||
  pathname.startsWith("/verify-email/") ||
  pathname.startsWith("/packages");

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const routeElement = useRoutes(routes);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  // Grab the post-auth redirect once so the polling loop and Hub listener
  // don't race to read/clear the same sessionStorage key.
  const postAuthRedirectRef = useRef<string | null>(null);

  const consumePostAuthRedirect = (): string => {
    if (postAuthRedirectRef.current) {
      const val = postAuthRedirectRef.current;
      postAuthRedirectRef.current = null;
      return val;
    }
    const val = localStorage.getItem('postAuthRedirect') || '/app';
    localStorage.removeItem('postAuthRedirect');
    postAuthRedirectRef.current = null;
    return val;
  };

  const checkAuth = useCallback(async () => {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
      if (!isPublicPath(window.location.pathname)) {
        navigate("/login", { replace: true });
      }
    }
  }, [navigate]);

  // Check authentication status on mount and handle OAuth callbacks.
  //
  // IMPORTANT: The enable-oauth-listener (imported in main.tsx) processes the
  // ?code=&state= params and exchanges them for tokens. It may complete and
  // fire the Hub "signInWithRedirect" event BEFORE this useEffect runs.
  // To handle both cases (Hub fires before or after we listen), we:
  //   1. Register the Hub listener first
  //   2. Then check if we're in an OAuth callback
  //   3. If so, wait briefly for the exchange, then poll getCurrentUser()
  //   4. If not an OAuth callback, just call checkAuth() normally
  useEffect(() => {
    // Snapshot OAuth params before Amplify may clear them
    const params = new URLSearchParams(window.location.search);
    const isOAuthCallback = params.has('code') && params.has('state');
    let oauthHandled = false;
    let oauthTimeout: ReturnType<typeof setTimeout> | null = null;

    // GitHub OAuth callbacks land on /settings/integrations with ?code=&state=.
    // Stash them in sessionStorage before Amplify's OAuth listener or auth guards
    // can strip them from the URL. GitHubSyncSection reads from sessionStorage as fallback.
    const isGitHubOAuthCallback = isOAuthCallback && location.pathname.includes('/settings/integrations');
    if (isGitHubOAuthCallback) {
      sessionStorage.setItem('github_oauth_code', params.get('code')!);
      sessionStorage.setItem('github_oauth_state_param', params.get('state')!);
    }

    // Only treat as Cognito OAuth if it's NOT a GitHub OAuth callback
    const isCognitoOAuth = isOAuthCallback && !isGitHubOAuthCallback;

    if (isCognitoOAuth) {
      // Snapshot the redirect target
      postAuthRedirectRef.current = localStorage.getItem('postAuthRedirect') || '/app';
      localStorage.removeItem('postAuthRedirect');
    }

    const handleOAuthSuccess = async () => {
      if (oauthHandled) return;
      oauthHandled = true;
      if (oauthTimeout) { clearTimeout(oauthTimeout); oauthTimeout = null; }
      try {
        await getCurrentUser();
        setIsAuthenticated(true);
        const redirectTo = consumePostAuthRedirect();
        navigate(redirectTo, { replace: true });
      } catch {
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      }
    };

    // Listen for auth changes
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      if (payload.event === "signInWithRedirect") {
        await handleOAuthSuccess();
      } else if (payload.event === "signedIn" && isCognitoOAuth) {
        // Only redirect on signedIn if we're in an OAuth callback flow.
        // For email/password sign-ins, the SignInForm handles its own navigation.
        await handleOAuthSuccess();
      } else if (payload.event === "signedIn" && !isCognitoOAuth) {
        // Email/password sign-in — SignInForm handles navigation.
        // Just update auth state here without navigating.
        setIsAuthenticated(true);
      } else if (payload.event === "signInWithRedirect_failure") {
        oauthHandled = true;
        if (oauthTimeout) { clearTimeout(oauthTimeout); oauthTimeout = null; }
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      } else if (
        payload.event === "signedOut" ||
        payload.event === "tokenRefresh_failure"
      ) {
        setIsAuthenticated(false);
        checkAuth();
      }
    });

    if (isCognitoOAuth) {
      // The enable-oauth-listener may have already exchanged the code before
      // our Hub listener was registered. Poll getCurrentUser() after a short
      // delay to catch that case.
      oauthTimeout = setTimeout(async () => {
        if (oauthHandled) return;
        try {
          await getCurrentUser();
          await handleOAuthSuccess();
        } catch {
          // Exchange hasn't completed yet. Give it more time.
          oauthTimeout = setTimeout(async () => {
            if (oauthHandled) return;
            try {
              await getCurrentUser();
              await handleOAuthSuccess();
            } catch {
              oauthHandled = true;
              setIsAuthenticated(false);
              navigate("/login", { replace: true });
            }
          }, 5000);
        }
      }, 2000);
    } else {
      // Normal (non-OAuth) auth check
      checkAuth();
    }

    return () => {
      unsubscribe();
      if (oauthTimeout) clearTimeout(oauthTimeout);
    };
  }, []); // Empty dependency array - only run once on mount

  // Public routes that don't require authentication
  const isPublicRoute = isPublicPath(location.pathname);

  // Handle /app route authentication manually
  useEffect(() => {
    if (location.pathname === "/app" && isAuthenticated === false) {
      navigate("/login", { replace: true });
    }
  }, [location.pathname, isAuthenticated, navigate]);

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <ThemeProvider theme={customTheme}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: '#666' }}>Loading...</p>
        </div>
      </ThemeProvider>
    );
  }

  if (isPublicRoute) {
    return (
      <ThemeProvider theme={customTheme}>
        <AnalyticsTracker />
        <AuthAnalyticsTracker />
        <AnalyticsStatus />
        {routeElement}
        <CookieConsent />
      </ThemeProvider>
    );
  }

  // All other routes require authentication
  return (
    <ThemeProvider theme={customTheme}>
      <UserAttributesProvider>
        <SubscriptionProvider>
          <MockDataProvider>
            <RatingDisplayProvider>
              <AnalyticsTracker />
              <AuthAnalyticsTracker />
              <AnalyticsStatus />
              <Layout>{routeElement}</Layout>
              <CookieConsent />
            </RatingDisplayProvider>
          </MockDataProvider>
        </SubscriptionProvider>
      </UserAttributesProvider>
    </ThemeProvider>
  );
}

export default App;
